import { Button, Dialog, Field } from '@noorden-insurtech/alba-design-system';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

/** Palavra exigida no input quando o chamador não informa outra. */
const DEFAULT_CONFIRMATION_WORD = 'confirmar';

/**
 * Pede um VALOR ao usuário em vez da palavra de confirmação (ex.: o nº da apólice
 * emitida manualmente no i4Pro). O valor digitado chega no `onConfirm`.
 */
export interface ConfirmActionValuePrompt {
  label: ReactNode;
  placeholder?: string;
  /** Teclado numérico no mobile e `inputMode` correto para números. */
  inputMode?: 'text' | 'numeric';
  /**
   * Validação local, antes de chamar a API. Devolva a mensagem de erro, ou
   * `undefined` quando o valor está aceitável. Erros do servidor continuam vindo
   * por `errorMessage`.
   */
  validate?: (value: string) => string | undefined;
}

export interface ConfirmActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Recebe o valor digitado quando há `valuePrompt`; string vazia no modo palavra. */
  onConfirm: (value: string) => void;
  title: string;
  /** O que exatamente vai acontecer, em uma ou duas frases. */
  description: ReactNode;
  /** Aviso extra destacado (ex.: excluir um número já utilizado). */
  warning?: ReactNode;
  /** Palavra que o usuário precisa digitar. Comparada sem diferenciar maiúsculas. */
  confirmationWord?: string;
  /**
   * Troca a barreira da palavra de confirmação pela coleta de um valor. Quando
   * presente, `confirmationWord` é ignorado.
   */
  valuePrompt?: ConfirmActionValuePrompt;
  confirmLabel?: string;
  /** `danger` pinta o botão de confirmação de vermelho — use em exclusão. */
  tone?: 'danger' | 'primary';
  isLoading?: boolean;
  /** Erro da API renderizado dentro do próprio modal, sem fechá-lo. */
  errorMessage?: string | null;
  /** Conteúdo extra abaixo do campo (ex.: anexar um arquivo). */
  children?: ReactNode;
  /**
   * `false` mantém o botão de confirmar desabilitado — para o que `children`
   * coleta e também é obrigatório. Padrão: `true`.
   */
  isExtraValid?: boolean;
}

/**
 * Confirmação de ação sensível: o botão de confirmar só habilita depois que o usuário
 * digita algo no campo — a palavra exigida (padrão) ou o valor pedido em `valuePrompt`.
 * Genérico de propósito: a barreira é a digitação, não o texto.
 */
export function ConfirmActionModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  warning,
  confirmationWord = DEFAULT_CONFIRMATION_WORD,
  valuePrompt,
  confirmLabel = 'Confirmar',
  tone = 'primary',
  isLoading = false,
  errorMessage,
  children,
  isExtraValid = true,
}: ConfirmActionModalProps) {
  const [typed, setTyped] = useState('');
  /** Só depois da primeira tentativa, para não acusar erro em campo intocado. */
  const [showValidation, setShowValidation] = useState(false);

  // Reabrir não pode herdar o que já foi digitado: confirmaria uma segunda ação sem
  // passar pela barreira.
  useEffect(() => {
    if (isOpen) {
      setTyped('');
      setShowValidation(false);
    }
  }, [isOpen]);

  const trimmed = typed.trim();

  const validationError = useMemo(() => {
    if (!valuePrompt) return undefined;
    if (!trimmed) return undefined;
    return valuePrompt.validate?.(trimmed);
  }, [valuePrompt, trimmed]);

  const canConfirm =
    isExtraValid &&
    (valuePrompt
      ? Boolean(trimmed) && !validationError && !isLoading
      : trimmed.toLowerCase() === confirmationWord.toLowerCase() && !isLoading);

  const handleConfirm = () => {
    setShowValidation(true);
    if (!canConfirm) return;
    onConfirm(trimmed);
  };

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open && !isLoading) onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[480px] max-w-[90vw]'>
          <Dialog.Header>
            <Dialog.Title>{title}</Dialog.Title>
          </Dialog.Header>

          <Dialog.Body className='space-y-4'>
            <div className='text-sm text-alba-gray-700'>{description}</div>

            {warning && (
              <div className='flex gap-2 rounded-lg border border-alba-warning-orange-400 bg-alba-warning-orange-400/10 p-3'>
                <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-alba-warning-orange-600' />
                <div className='text-sm text-alba-gray-800'>{warning}</div>
              </div>
            )}

            <Field.Root>
              <Field.Label>
                {valuePrompt ? (
                  valuePrompt.label
                ) : (
                  <>
                    Para confirmar a ação, digite{' '}
                    <span className='font-semibold text-alba-black-500'>
                      {confirmationWord}
                    </span>{' '}
                    no campo abaixo
                  </>
                )}
              </Field.Label>
              <Field.Input
                autoFocus
                value={typed}
                placeholder={valuePrompt?.placeholder ?? confirmationWord}
                inputMode={valuePrompt?.inputMode}
                autoComplete='off'
                disabled={isLoading}
                onChange={event => setTyped(event.target.value)}
                onKeyDown={event => {
                  // O app registra hotkeys globais; sem isso, digitar dispara atalho.
                  event.stopPropagation();
                  if (event.key === 'Enter') handleConfirm();
                }}
              />
              {showValidation && validationError && (
                <p className='text-sm text-alba-error-red-600'>
                  {validationError}
                </p>
              )}
            </Field.Root>

            {children}

            {errorMessage && (
              <p className='rounded-lg border border-alba-error-red-400 bg-alba-error-red-400/10 p-3 text-sm text-alba-error-red-600'>
                {errorMessage}
              </p>
            )}
          </Dialog.Body>

          <Dialog.Footer className='flex justify-end gap-2'>
            <Button
              variant='ghost'
              size='md'
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              variant='primary'
              size='md'
              onClick={handleConfirm}
              disabled={!canConfirm}
              loading={isLoading}
              className={
                tone === 'danger'
                  ? '!bg-alba-error-red-500 hover:!bg-alba-error-red-600 disabled:!bg-alba-error-red-400'
                  : undefined
              }
            >
              {confirmLabel}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
